/**
 * Consola de Solicitudes — panel del usuario tipo `administrador_solicitudes`
 * dentro del Portal Cliente. Ve todas las solicitudes de su misma empresa,
 * puede APROBAR (genera el ticket real para Traveldesk) o CANCELAR (con motivo
 * obligatorio, notifica al solicitante).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, XCircle, RefreshCw, Inbox, Eye, MailCheck, FileText, Plus, ArrowLeft } from "lucide-react";
import SolicitudCotizacion from "@/pages/SolicitudCotizacion";

const STATUS_STYLES = {
  pending:  { label: "Pendiente",  cls: "bg-amber-100 text-amber-800 border-amber-300" },
  approved: { label: "Aprobada",   cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  rejected: { label: "Cancelada",  cls: "bg-red-100 text-red-800 border-red-300" },
};

const ConsolaSolicitudes = ({ authed }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [detail, setDetail] = useState(null); // { ticket, submission }
  const [action, setAction] = useState(null); // { kind: 'aprobar'|'cancelar', ticket }
  const [motivo, setMotivo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [creating, setCreating] = useState(false); // el admin creando su propia solicitud

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/forms/external/solicitudes?status=${filter}`, { withCredentials: true });
      setItems(r.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar el listado");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openDetail = async (ticketId) => {
    try {
      const r = await axios.get(`${API}/forms/external/solicitudes/${ticketId}`, { withCredentials: true });
      setDetail(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar la solicitud");
    }
  };

  const openAction = (kind, ticket) => {
    setAction({ kind, ticket });
    setMotivo("");
  };

  const submitAction = async () => {
    if (!action) return;
    if (action.kind === "cancelar" && !motivo.trim()) {
      toast.error("El motivo es obligatorio para cancelar");
      return;
    }
    setSubmitting(true);
    try {
      const url = `${API}/forms/external/solicitudes/${action.ticket.id}/${action.kind}`;
      await axios.post(url, { motivo: motivo.trim() }, { withCredentials: true });
      toast.success(action.kind === "aprobar" ? "Solicitud aprobada · ticket enviado a Traveldesk" : "Solicitud cancelada");
      setAction(null);
      setMotivo("");
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo procesar");
    } finally {
      setSubmitting(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {creating ? (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setCreating(false); fetchItems(); }}
              data-testid="consola-back-to-list"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />Volver a la consola
            </Button>
            <p className="text-xs text-slate-500 italic">
              Tu solicitud entrará a la consola como <b>pendiente</b> — deberás aprobarla tú mismo.
            </p>
          </div>
          <SolicitudCotizacion
            authed={authed}
            onLogout={() => { setCreating(false); fetchItems(); }}
          />
        </div>
      ) : (
      <>
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <MailCheck className="w-5 h-5 text-indigo-600" />
            Consola de Solicitudes
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Aprueba o cancela las solicitudes de cotización de <b>{authed?.empresa}</b> antes de que se envíen a PD TRAVEL.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => setCreating(true)}
            data-testid="consola-btn-nueva-solicitud"
          >
            <Plus className="w-4 h-4 mr-1.5" />Nueva solicitud
          </Button>
          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" data-testid="consola-status-filter">
            {[
              ["pending", "Pendientes"],
              ["approved", "Aprobadas"],
              ["rejected", "Canceladas"],
              ["all", "Todas"],
            ].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${filter === k ? "bg-indigo-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                data-testid={`consola-filter-${k}`}
              >
                {l}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={fetchItems} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            <p className="text-sm">Cargando solicitudes…</p>
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="text-sm">No hay solicitudes {filter === "pending" ? "pendientes" : filter === "all" ? "" : filter === "approved" ? "aprobadas" : "canceladas"}.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="text-left p-2.5">Folio</th>
                  <th className="text-left p-2.5">Solicitante</th>
                  <th className="text-left p-2.5">Título</th>
                  <th className="text-left p-2.5">Fecha</th>
                  <th className="text-left p-2.5">Estatus</th>
                  <th className="text-right p-2.5">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => {
                  const s = STATUS_STYLES[t.approval_status] || STATUS_STYLES.pending;
                  return (
                    <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`solicitud-row-${t.folio}`}>
                      <td className="p-2.5 font-mono text-xs text-slate-800">{t.folio}</td>
                      <td className="p-2.5">
                        <div className="text-slate-800">{t.created_by_name || "—"}</div>
                        <div className="text-xs text-slate-500">{t.created_by_email || ""}</div>
                      </td>
                      <td className="p-2.5 text-slate-700 max-w-[260px] truncate" title={t.title}>{t.title || "—"}</td>
                      <td className="p-2.5 text-xs text-slate-600">{fmtDate(t.created_at)}</td>
                      <td className="p-2.5">
                        <Badge variant="outline" className={`${s.cls} border`}>{s.label}</Badge>
                      </td>
                      <td className="p-2.5">
                        <div className="flex justify-end gap-1.5">
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => openDetail(t.id)}>
                            <Eye className="w-3.5 h-3.5 mr-1" />Ver
                          </Button>
                          {t.approval_status === "pending" && (
                            <>
                              <Button size="sm" className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => openAction("aprobar", t)} data-testid={`btn-aprobar-${t.folio}`}>
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Aprobar
                              </Button>
                              <Button size="sm" variant="destructive" className="h-7 px-2 text-xs" onClick={() => openAction("cancelar", t)} data-testid={`btn-cancelar-${t.folio}`}>
                                <XCircle className="w-3.5 h-3.5 mr-1" />Cancelar
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              {detail?.ticket?.folio} · {detail?.ticket?.title}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="max-h-[65vh] overflow-y-auto">
              <div
                className="prose prose-sm max-w-none border border-slate-200 rounded-md p-4 bg-slate-50"
                dangerouslySetInnerHTML={{ __html: detail?.ticket?.description || "<p>Sin detalle</p>" }}
              />
              {detail?.ticket?.approval_motivo && (
                <div className="mt-3 p-3 rounded-md border border-amber-200 bg-amber-50 text-sm">
                  <b className="text-amber-900">Nota del administrador:</b> {detail.ticket.approval_motivo}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action dialog (approve / cancel) */}
      <Dialog open={!!action} onOpenChange={(v) => !v && setAction(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {action?.kind === "aprobar" ? "Aprobar solicitud" : "Cancelar solicitud"}
              {" — "}<span className="font-mono text-sm">{action?.ticket?.folio}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {action?.kind === "aprobar"
                ? "Al aprobar, se enviará el ticket al equipo Traveldesk para su cotización y se notificará al solicitante."
                : "Se marcará la solicitud como cancelada, no se enviará al equipo Traveldesk y el solicitante recibirá un correo con tu motivo."}
            </p>
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1">
                {action?.kind === "aprobar" ? "Nota (opcional)" : "Motivo (obligatorio)"}
              </label>
              <Textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={4}
                placeholder={action?.kind === "aprobar" ? "Cualquier nota adicional para el equipo Traveldesk…" : "Explica al solicitante por qué cancelas su solicitud…"}
                data-testid="consola-motivo-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAction(null)} disabled={submitting}>Cerrar</Button>
              <Button
                onClick={submitAction}
                disabled={submitting || (action?.kind === "cancelar" && !motivo.trim())}
                className={action?.kind === "aprobar" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
                data-testid="consola-motivo-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : (action?.kind === "aprobar" ? <CheckCircle2 className="w-4 h-4 mr-1" /> : <XCircle className="w-4 h-4 mr-1" />)}
                {action?.kind === "aprobar" ? "Aprobar y enviar a Traveldesk" : "Cancelar solicitud"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
};

export default ConsolaSolicitudes;
